import React, { useState } from "react";
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import { createPortal } from 'react-dom';

const IraPendingHelpDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen(!isOpen);

  const drawer = (
    <div className="drawer-portal">
      <Drawer
        open={isOpen}
        onClose={toggle}
        direction="right"
        size={500}
        duration={400}
        overlayOpacity={0.5}
        overlayColor="#000"
        customIdSuffix="help-guide-ira-pending"
        className="help-guide-drawer"
        style={{ top: "73px", height: "calc(100vh - 73px)", transition: "all 0.4s ease-in-out" }}
      >
        <div className="drawer-content">
          <div className="drawer-header">
            <div className="header-title">
              <span className="emoji">🟡</span>
              <span className="title">IRA – Pending Tab Help</span>
            </div>
            <button className="close-button" onClick={toggle} aria-label="Close help panel">
              &times;
            </button>
          </div>

          <div className="drawer-body">
  <p>
    <strong>Help – Pending Accident IRA Detail Page</strong>
  </p>

  <p>
    The Pending Accident IRA Detail Page displays the record of an accident that has been reported 
    but where the investigation is yet to start. This page allows the company to review the reported 
    details, understand the context, and assign the investigation team.
  </p>

  <hr />

  <h5 className="section-title">📄 Record Overview</h5>
  <p>At the top of the page, you will see:</p>
  <ul className="steps-list">
    <li>• Record Number – e.g., 940</li>
    <li>• IRA Type – UA / UC (Unsafe Act / Unsafe Condition)</li>
    <li>• Activity Name – e.g., Bar Bending</li>
    <li>• Accident Reported Month – e.g., July</li>
  </ul>

  <p>Below this, the timeline displays key milestones:</p>
  <ul className="steps-list">
    <li>• Reported Details</li>
    <li>• Incident Date</li>
    <li>• Injured Person Details</li>
    <li>• Discussion Comments (if any)</li>
  </ul>

  <hr />

  <h5 className="section-title">📝 Reported Details Section</h5>
  <p>This section captures the original accident report:</p>
  <ul className="steps-list">
    <li>• Evidence – uploaded images of the accident (if available).</li>
    <li>• Incident Description – summary of what happened.</li>
  </ul>

  <hr />

  <h5 className="section-title">🏗️ Activity Information Section</h5>
  <p>Shows organizational context of the reported accident:</p>
  <ul className="steps-list">
    <li>• Service and its linked Plant/Project/Client.</li>
    <li>• Department/Contractor/Location for which the accident was reported.</li>
  </ul>

  <hr />

  <h5 className="section-title">⚠️ Incident Details Section</h5>
  <p>Provides factual information on the accident:</p>
  <ul className="steps-list">
    <li>• Incident Date – when the accident occurred.</li>
    <li>• Incident Time – exact time of occurrence.</li>
    <li>• Injured Person Details – who was injured and their role/identity.</li>
  </ul>

  <hr />

  <h5 className="section-title">💬 Discussion Section</h5>
  <p>The Discussion section acts as a chat space for team members.</p>
  <ul className="steps-list">
    <li>• Team members can exchange thoughts, strategies, and ideas on how to investigate the accident.</li>
    <li>• Helps ensure team collaboration and focus on preventing recurrence of similar accidents.</li>
  </ul>

  <hr />

  <h5 className="section-title">📊 UAUC Detail Analysis (Right-hand Panel)</h5>
  <p>The right-hand side displays supporting analytics linked to the reported activity:</p>
  <ul className="steps-list">
    <li>• IRA Analysis – accident statistics.</li>
    <li>• Near Miss Analysis – near miss reporting patterns.</li>
    <li>• UA vs UC Analysis – ratio of unsafe acts vs unsafe conditions.</li>
    <li>• Risk-Based Analysis – accidents by risk severity.</li>
    <li>• Good Act & TBT Count – positive observations and toolbox talks.</li>
    <li>• Internal & External Training Count – trainings delivered.</li>
    <li>• Employee & Contractor Training Count – workforce training coverage.</li>
  </ul>

  <p>This information helps teams use data-driven insights for preventive action.</p>

  <hr />

  <h5 className="section-title">🛠️ Steps to Start an Accident Investigation</h5>

  <p><strong>Step 1 – Assign Investigation Team</strong></p>
  <ul className="steps-list">
    <li>• Go to the top-right corner and click on the Add Team icon.</li>
    <li>• Add the required investigation team members.</li>
    <li>• Once added, the members appear in the Investigation Team section (above the Analysis tab on the right).</li>
  </ul>

  <hr />

  <p><strong>Step 2 – Assign Team Lead</strong></p>
  <ul className="steps-list">
    <li>• Each team member’s profile will show a crown icon.</li>
    <li>• To assign a Team Lead, click on the crown icon next to the member’s name.</li>
    <li>• The Team Lead is responsible for leading and coordinating the investigation.</li>
  </ul>

  <hr />

  <p><strong>Step 3 – Define Target Date</strong></p>
  <ul className="steps-list">
    <li>• Once a Team Lead is assigned, a pop-up will appear prompting for a Target Date.</li>
    <li>• Enter the date by which the investigation should be completed (up to CAPA recommendation).</li>
    <li>• This helps track the investigation timeline from the reported date → CAPA implementation.</li>
  </ul>

  <div className="alert alert-info">
    <b>✅ Note:</b> The accident remains in Pending status until the investigation team is assigned 
    and the process officially begins.
  </div>
</div>

        </div>
      </Drawer>
    </div>
  );

  return (
    <>
      <div
        className="toggle-help-btn"
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === "Enter" || e.key === " ") toggle();
        }}
        style={{ fontSize: "16px", cursor: "pointer" }}
      >
        Page Help
        <img src="/images/formkit_help.svg" alt="" />
      </div>
      {createPortal(drawer, document.body)}
    </>
  );
};

export default IraPendingHelpDrawer;
